import { groupBy, isEmpty } from 'lodash-es';
import moment from 'moment';
import React from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';

import { IDashboardHomeCardMetricKey } from '~/src/dash/api/dashboards/home';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import {
  CHART_COLORS,
  formatTooltipDate,
  formatTooltipValue,
  getScalesInfo,
  IDashboardChartMetric,
} from '~/src/dash/components/charts/common';
import { LoadingChart } from '~/src/dash/components/charts/LoadingChart';
import { Col, Cols, fontSizing, Row, Rows, spacing, Text, usePanel } from '~/src/dash/publicaUI';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { calculateTrend, displayArrow, getTrendColors } from '~/src/dash/utils/trends';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const renderItems = (
  metrics: IDashboardChartMetric[],
  numItems: number,
  isCompareEnabled?: boolean,
) => {
  const bars: JSX.Element[] = [];
  const lines: JSX.Element[] = [];
  metrics.forEach((metric) => {
    const colorIndex = metric.chartColorIndex;
    const yAxisId = metric.yScale || 'base';
    if (metric.chartType === 'bar') {
      bars.push(
        <Bar
          name={metric.label}
          dataKey={`main.${metric.key}`}
          barSize={24}
          fill={CHART_COLORS[colorIndex][0]}
          yAxisId={yAxisId}
        />,
      );
      if (isCompareEnabled) {
        bars.push(
          <Bar
            name={metric.label}
            dataKey={`compare.${metric.key}`}
            barSize={24}
            fill={CHART_COLORS[colorIndex][1]}
            yAxisId={yAxisId}
          />,
        );
      }
    }
    if (metric.chartType === 'line') {
      if (isCompareEnabled) {
        lines.push(
          <Line
            name={metric.label}
            type='monotone'
            dataKey={`compare.${metric.key}`}
            stroke={CHART_COLORS[colorIndex][1]}
            fill={CHART_COLORS[colorIndex][1]}
            strokeWidth={3}
            dot={false}
            yAxisId={yAxisId}
            strokeDasharray='4 4'
          />,
        );
      }
      lines.push(
        <Line
          name={metric.label}
          type='monotone'
          dataKey={`main.${metric.key}`}
          stroke={CHART_COLORS[colorIndex][0]}
          fill={CHART_COLORS[colorIndex][0]}
          strokeWidth={3}
          activeDot={{ r: 8 }}
          dot={numItems <= 15}
          yAxisId={yAxisId}
        />,
      );
    }
  });
  return [...bars, ...lines];
};

interface ITrendDisplayProps {
  value: number | null;
  isInverseTrend?: boolean;
}

const TrendDisplay = ({ value, isInverseTrend }: ITrendDisplayProps) => {
  if (!value) {
    return <Text extraSmall bold color={publicaColors.gray} disableSelect></Text>;
  }
  const colors = getTrendColors(value, isInverseTrend);
  return (
    <Cols
      gap='4px'
      style={{
        alignItems: 'center',
        backgroundColor: colors.background,
        borderRadius: '4px',
        padding: '1px 6px 0px 4px',
      }}
    >
      <Col displayFlex width={16}>
        {displayArrow(value, colors.text)}
      </Col>
      <Col>
        <Text extraSmall bold color={colors.text} disableSelect>
          {`${Math.abs(value).toFixed(2)}%`}
        </Text>
      </Col>
    </Cols>
  );
};

interface ICustomTooltipProps {
  active: boolean;
  payload: any;
  label: string;
  metricsConfig: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
  grouping: number;
  currencySymbol: string;
}

const CustomTooltip = ({
  active,
  payload,
  metricsConfig,
  grouping,
  currencySymbol,
}: ICustomTooltipProps) => {
  if (active && !isEmpty(payload)) {
    const groupedPayloads = groupBy(payload, 'name');

    return (
      <div
        style={{
          backgroundColor: publicaColors.white,
          border: `1px solid ${publicaColors.lightGray}`,
          padding: `${spacing.small} ${spacing.base}`,
        }}
      >
        {Object.entries(groupedPayloads).map((array: any) => {
          const [label, payload] = array;
          let orderedPayload = payload;
          const metricInfo = Object.values(metricsConfig).find((metric) => label === metric.label);
          // in order to prioritize the line chart, we have to flip the values on the tooltip to move the main date up
          if (metricInfo?.chartType === 'line') {
            orderedPayload = payload.reverse();
          }
          let trendValue = 0;
          let isInverseTrend = false;
          if (orderedPayload.length === 2) {
            isInverseTrend = !!metricInfo?.isInverseTrend;
            const [mainValue, compareValue] = orderedPayload.map((item: any) => item.value);
            trendValue = calculateTrend(mainValue, compareValue);
          }

          return (
            <Rows paddingBottom={spacing.small}>
              <Row
                noPadding
                marginBottom={spacing.small}
                style={{
                  borderBottom: `1px solid ${publicaColors.lightGray}`,
                }}
              >
                <Cols
                  gap={spacing.large}
                  marginBottom={spacing.extraSmall}
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Col>
                    <Text small bold>
                      {label}
                    </Text>
                  </Col>
                  <Col>
                    <TrendDisplay value={trendValue} isInverseTrend={isInverseTrend} />
                  </Col>
                </Cols>
              </Row>
              {orderedPayload.map((item: any) => {
                const keys = item.dataKey.split('.');
                const [range, metricKey] = keys;
                const date = formatTooltipDate(item.payload[range].Date, grouping);
                const valueType = metricsConfig[metricKey as IDashboardHomeCardMetricKey].valueType;
                const value = formatTooltipValue(
                  item.payload[range][metricKey],
                  valueType,
                  currencySymbol,
                );
                return (
                  <Row noPadding>
                    <Cols
                      gap={spacing.extraLarge}
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Col>
                        <Cols gap='4px' style={{ alignItems: 'center' }}>
                          <Col paddingBottom='1px'>
                            <Text color={item.fill} style={{ fontSize: fontSizing.mini }}>
                              ⬤
                            </Text>
                          </Col>
                          <Col>
                            <Text small bold>
                              {date}
                            </Text>
                          </Col>
                        </Cols>
                      </Col>
                      <Col>
                        <Text small>{value}</Text>
                      </Col>
                    </Cols>
                  </Row>
                );
              })}
            </Rows>
          );
        })}
      </div>
    );
  }
  return null;
};

interface ICompareChartProps {
  isCompareEnabled?: boolean;
  loadState: LoadingState;
  metrics: IDashboardChartMetric[];
  data: Array<Record<string, any>>;
  metricsConfig: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
  grouping: number;
  syncID?: string;
}

export const CompareChart = ({
  isCompareEnabled,
  data = [],
  metrics,
  metricsConfig,
  grouping,
  loadState,
  syncID = '',
}: ICompareChartProps) => {
  const { width } = usePanel();
  const { currencySymbol } = useBaseStore();
  const { scales, numVisibleAxes } = getScalesInfo(metrics);

  const formatTimestamp = (date: string | moment.Moment): string => {
    return !date
      ? ''
      : moment(date)
          .utc()
          .format(grouping ? 'M/D' : 'M/D HH:mm');
  };

  return (
    <div
      style={{
        width: '100%',
        height: '280px',
      }}
    >
      {loadState === LoadingStateLoading ? (
        <LoadingChart />
      ) : (
        <ComposedChart
          width={(width || 0) - 80}
          height={280}
          data={data}
          barGap={4}
          margin={{
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
          }}
          syncId={syncID}
        >
          <Tooltip
            content={
              // this bypasses an issue with automated props from recharts
              // @ts-ignore
              <CustomTooltip
                metricsConfig={metricsConfig}
                grouping={grouping}
                currencySymbol={currencySymbol}
              />
            }
            cursor={{ fill: publicaColors.lightGray }}
          />
          <CartesianGrid strokeDasharray='3 3' vertical={false} />
          {renderItems(metrics, data.length, isCompareEnabled)}
          <XAxis
            dataKey='main.Date'
            tick={{
              fontSize: 12,
              fill: 'black',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            // @ts-ignore
            tickFormatter={formatTimestamp}
            domain={[0, data.length]}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{ fontSize: 12, fill: 'black', textAnchor: 'end' }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='base'
            hide={scales.base.hide}
            orientation='left'
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{
              fontSize: 12,
              fill: 'black',
              textAnchor: scales.large.orientation === 'left' ? 'end' : 'start',
            }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='large'
            hide={scales.large.hide}
            orientation={scales.large.orientation}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{
              fontSize: 12,
              fill: 'black',
              textAnchor: scales.small.orientation === 'left' ? 'end' : 'start',
            }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='small'
            hide={scales.small.hide}
            orientation={scales.small.orientation}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            yAxisId='placeholder-left'
            hide={metrics.length !== 0}
            orientation='left'
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            yAxisId='placeholder-right'
            hide={numVisibleAxes > 1}
            orientation='right'
          />
        </ComposedChart>
      )}
    </div>
  );
};
